
import Vue from 'vue';
import {Component, Watch, Prop} from 'vue-property-decorator';
import * as Audio from '../../../../audio/index';
import { type AbstractPlayableContent } from 'src/modules/playable/models/AbstractPlayableContent';
import { analyticsController } from 'src/modules/user/analytics/AnalyticsController';
import { isInMobileApp } from 'src/utils/MobileUtils';
import { userService } from 'src/modules/user/UserService';
import audioPlayer from 'src/modules/synth/AudioPlayer';
import { playerService } from '../../../models/PlayerService';
import { getAssetItem } from 'src/utils/helpers';

@Component({
  methods: {getAssetItem}
})
export default class AudioCaptureButton extends Vue {
  @Prop() size
  @Prop({ type: Boolean, default: false }) readonly isInPlayer!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isListening!: boolean;

  private loading = false;
  private isExplanationDialogDisplayed = false;
  private doNotShowAgain: boolean = false
  private isNotAllowedMicroDialogDisplayed = false;

  mounted() {
    this.$root.$on('activate_audio_capture', this.showPopup)
  }

  beforeDestroy() {
    this.onPlayableContentChange(null, null)
    this.$root.$off('activate_audio_capture', this.showPopup)
  }

  get playerPlayableContent() {
    return playerService.state.playerPlayableContent
  }
  get isPlayerPlay() {
    return audioPlayer.state.playerStatus === 'play' || audioPlayer.state.playerStatus === 'countdown';
  }
  get isTranscriptionWorkerReady() {
    return Audio.musicTranscriptionController.state.workerState === 'ready';
  }
  get isTranscriptionWorkerLoading() {
    return Audio.musicTranscriptionController.state.workerState === 'loading';
  }
  get performanceTestValid() {
    return Audio.musicTranscriptionController.state.performanceTestValid;
  }
  get isPerformanceTestProcessing() {
    return Audio.musicTranscriptionController.state.isPerformanceTestProcessing;
  }

  async executePerformanceTest(fromAllowedMicro = false) {
    analyticsController.logBasicAnalyticsEvent('started audio performance test', 'audio capture performance test', { source: 'player' })
    const response = await Audio.musicTranscriptionController.executePerformanceTest();

    if(response === 'notAllowed' && fromAllowedMicro) {
      this.$q.notify({
        progress: false,
        timeout: 2000,
        color: 'orange',
        message: "Vous devez autoriser le micro pour pouvoir utiliser le mode écoute ",
        icon: 'mdi-ear-hearing',
        position: isInMobileApp() ? 'top' : 'bottom',
      });
    }
    else if (fromAllowedMicro) {
      await this.showPopup();
      this.isNotAllowedMicroDialogDisplayed = false
    }
  }
  get displayOnboarding() {
    return !userService.user.configurations.onboardingConfig.onboardingSlidesCompleted.includes('keyboardSlide')
  }

  async showPopup() {
    if (this.displayOnboarding) {
      this.$root.$emit('displayKeyboardOnboarding')
      return
    }

    if(this.performanceTestValid === 'notMade' || this.performanceTestValid === 'reported') {
      await this.executePerformanceTest()
    }

    if(this.performanceTestValid === 'notValidated') {
      this.$q.notify({
        progress: false,
        timeout: 2000,
        color: 'orange',
        message: "Le mode écoute n'est pas compatible avec votre appareil",
        icon: 'mdi-ear-hearing',
        position: isInMobileApp() ? 'top' : 'bottom',
      });
    }

    if (this.performanceTestValid === 'notAllowed') {
      this.isNotAllowedMicroDialogDisplayed = true
      if(this.$q.platform.is.desktop) {
        const audioCapturePerformanceOptimalPeriod = localStorage.audioCapturePerformanceOptimalPeriod && JSON.parse(localStorage.audioCapturePerformanceOptimalPeriod)
        if(audioCapturePerformanceOptimalPeriod) {
          localStorage.audioCapturePerformanceOptimalPeriod = JSON.stringify({...audioCapturePerformanceOptimalPeriod, status: 'reported'})
        }
      }
    }

    else {
      await this.change();
    }
  }

  async change() {
    // TODO: audio add analytics
    this.loading = true;

    if (this.isTranscriptionWorkerReady) {
      analyticsController.logBasicAnalyticsEvent('clicked on listening button', 'audio capture usage', { state: 'inactive' })
      Audio.musicTranscriptionController.terminate();
    }
    else {
      analyticsController.logBasicAnalyticsEvent('clicked on listening button', 'audio capture usage', { state: 'active' })
      const response = await Audio.musicTranscriptionController.init();
      localStorage.removeItem('toursWithoutAudioCapture')

      if(response === 'notAllowed') {
        this.isNotAllowedMicroDialogDisplayed = true
      }
    }

    this.isExplanationDialogDisplayed = false;

    if (this.doNotShowAgain) {
      localStorage.noLongerDisplayPopupAudioCapture = true;
    }
    this.loading = false
  }

  @Watch('isTranscriptionWorkerReady')
  onTranscriptionWorkerStateChange(value) {
    if (!this.isPerformanceTestProcessing && (this.performanceTestValid === 'validated' || this.performanceTestValid === 'notValidated')) {
      this.$root.$emit('teacher_suggestion', {
        message: value ? "J'ai activé l'écoute de vos notes, je désactive le son pour bien vous entendre." : "J'ai désactivé le mode écoute.",
        timeout: 5000,
      })
    }
  }

  @Watch('playerPlayableContent')
  onPlayableContentChange(_, oldValue: AbstractPlayableContent | null) {
    if(oldValue !== null) {
      Audio.musicTranscriptionController.terminate();
    }
  }
}
